import axios from 'axios';

// Configuration de l'API
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour la gestion des erreurs
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Erreur API:', error);
    console.error('Status:', error.response?.status);
    console.error('Data:', error.response?.data);
    
    // Si c'est une erreur 401 (non autorisé) et qu'on n'est pas sur /auth/login
    if (error.response?.status === 401 && !error.config?.url?.includes('/auth/login')) {
      console.log('Erreur 401 détectée, nettoyage de l\'authentification');
      authService.logout();
    }
    
    return Promise.reject(error);
  }
);

// Services pour les projets
export const projetsService = {
  // Récupérer tous les projets
  getAll: async () => {
    try {
      const response = await api.get('/projets');
      return response.data; // Les listes gardent le format avec pagination
    } catch (error) {
      throw new Error('Erreur lors de la récupération des projets');
    }
  },

  // Récupérer un projet par ID
  getById: async (id) => {
    try {
      const response = await api.get(`/projets/${id}`);
      console.log(response.data)
      return response.data;
    } catch (error) {
      throw new Error(`Erreur lors de la récupération du projet ${id}`);
    }
  },

  // Créer un nouveau projet
  create: async (projet) => {
    try {
      console.log('🔍 Debug create projet frontend:');
      console.log('  - Token présent:', !!api.defaults.headers.common['Authorization']);
      console.log('  - Headers:', api.defaults.headers.common);
      console.log('  - Données projet:', projet);
      
      const response = await api.post('/projets', projet);
      return response.data.data; // Extraire le projet du wrapper API
    } catch (error) {
      console.error('❌ Erreur création projet:', error.response?.data);
      throw new Error('Erreur lors de la création du projet');
    }
  },

  // Mettre à jour un projet
  update: async (id, projet) => {
    try {
      const response = await api.put(`/projets/${id}`, projet);
      return response.data.data; // Extraire le projet du wrapper API
    } catch (error) {
      throw new Error(`Erreur lors de la mise à jour du projet ${id}`);
    }
  },

  // Supprimer un projet
  delete: async (id) => {
    try {
      const response = await api.delete(`/projets/${id}`);
      return response.data; // Retourner la réponse complète pour savoir si c'est une suppression ou un archivage
    } catch (error) {
      throw new Error(`Erreur lors de la suppression du projet ${id}`);
    }
  },

  // Récupérer les statistiques d'un projet
  getStats: async (id) => {
    try {
      const response = await api.get(`/projets/${id}/stats`);
      return response.data;
    } catch (error) {
      throw new Error(`Erreur lors de la récupération des statistiques du projet ${id}`);
    }
  },
};

// Services pour les personnes
export const personnesService = {
  // Récupérer toutes les personnes
  getAll: async () => {
    try {
      const response = await api.get('/personnes');
      return response.data;
    } catch (error) {
      throw new Error('Erreur lors de la récupération des personnes');
    }
  },

  // Récupérer une personne par ID
  getById: async (id) => {
    try {
      const response = await api.get(`/personnes/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(`Erreur lors de la récupération de la personne ${id}`);
    }
  },

  // Créer une nouvelle personne
  create: async (personne) => {
    try {
      const response = await api.post('/personnes', personne);
      return response.data.data; // Extraire la personne du wrapper API
    } catch (error) {
      throw new Error('Erreur lors de la création de la personne');
    }
  },

  // Mettre à jour une personne
  update: async (id, personne) => {
    try {
      const response = await api.put(`/personnes/${id}`, personne);
      return response.data.data; // Extraire la personne du wrapper API
    } catch (error) {
      throw new Error(`Erreur lors de la mise à jour de la personne ${id}`);
    }
  },

  // Supprimer une personne
  delete: async (id) => {
    try {
      await api.delete(`/personnes/${id}`);
    } catch (error) {
      throw new Error(`Erreur lors de la suppression de la personne ${id}`);
    }
  },

  // Récupérer les statistiques d'une personne
  getStats: async (id) => {
    try {
      const response = await api.get(`/personnes/${id}/stats`);
      return response.data;
    } catch (error) {
      throw new Error(`Erreur lors de la récupération des statistiques de la personne ${id}`);
    }
  },

  // Récupérer les équipes d'une personne
  getEquipes: async (id) => {
    try {
      const response = await api.get(`/personnes/${id}/equipes`);
      return response.data;
    } catch (error) {
      throw new Error(`Erreur lors de la récupération des équipes de la personne ${id}`);
    }
  },

  // Ajouter une personne à une équipe
  addToEquipe: async (personneId, equipeData) => {
    try {
      const response = await api.post(`/personnes/${personneId}/equipes`, equipeData);
      return response.data;
    } catch (error) {
      throw new Error(`Erreur lors de l'ajout de la personne ${personneId} à l'équipe`);
    }
  },

  // Retirer une personne d'une équipe
  removeFromEquipe: async (personneId, equipeId) => {
    try {
      await api.delete(`/personnes/${personneId}/equipes/${equipeId}`);
    } catch (error) {
      throw new Error(`Erreur lors du retrait de la personne ${personneId} de l'équipe ${equipeId}`);
    }
  },
};

// Services pour les rattachements
export const rattachementsService = {
  // Récupérer tous les rattachements
  getAll: async () => {
    try {
      const response = await api.get('/rattachements');
      return response.data;
    } catch (error) {
      throw new Error('Erreur lors de la récupération des rattachements');
    }
  },

  // Récupérer les rattachements d'un projet
  getByProjet: async (projetId) => {
    try {
      const response = await api.get(`/rattachements/projet/${projetId}`);
      return response.data;
    } catch (error) {
      throw new Error(`Erreur lors de la récupération des rattachements du projet ${projetId}`);
    }
  },

  // Créer un nouveau rattachement
  create: async (rattachement) => {
    try {
      const response = await api.post('/rattachements', rattachement);
      return response.data.data; // Extraire le rattachement du wrapper API
    } catch (error) {
      throw new Error('Erreur lors de la création du rattachement');
    }
  },

  // Supprimer un rattachement
  delete: async (id) => {
    try {
      await api.delete(`/rattachements/${id}`);
    } catch (error) {
      throw new Error(`Erreur lors de la suppression du rattachement ${id}`);
    }
  },
};

// Services pour les temps planifiés
export const tempsPlanifiesService = {
  // Récupérer tous les temps planifiés avec filtres optionnels
  getAll: async (params = {}) => {
    try {
      const response = await api.get('/temps-planifies', { params });
      return response.data;
    } catch (error) {
      throw new Error('Erreur lors de la récupération des temps planifiés');
    }
  },

  // Récupérer les temps planifiés par projet
  getByProjet: async (projetId) => {
    try {
      const response = await api.get(`/temps-planifies/projet/${projetId}`);
      return response.data;
    } catch (error) {
      throw new Error(`Erreur lors de la récupération des temps planifiés du projet ${projetId}`);
    }
  },

  // Créer ou mettre à jour les temps planifiés
  upsert: async (projetId, semaine, temps) => {
    try {
      const response = await api.post(`/temps-planifies`, {
        id_projet: projetId,
        semaine,
        ...temps,
      });
      return response.data.data; // Extraire les temps planifiés du wrapper API
    } catch (error) {
      throw new Error('Erreur lors de la sauvegarde des temps planifiés');
    }
  },

  // Supprimer les temps planifiés
  delete: async (id) => {
    try {
      await api.delete(`/temps-planifies/${id}`);
    } catch (error) {
      throw new Error(`Erreur lors de la suppression des temps planifiés ${id}`);
    }
  },
};

// Services pour les temps consommés
export const tempsConsommesService = {
  // Récupérer tous les temps consommés
  getAll: async (params = {}) => {
    try {
      const response = await api.get('/temps-consommes', { params });
      return response.data;
    } catch (error) {
      throw new Error('Erreur lors de la récupération des temps consommés');
    }
  },

  // Récupérer les temps consommés par projet
  getByProjet: async (projetId) => {
    try {
      const response = await api.get(`/temps-consommes/projet/${projetId}`);
      return response.data;
    } catch (error) {
      throw new Error(`Erreur lors de la récupération des temps consommés du projet ${projetId}`);
    }
  },

  // Récupérer les temps consommés par personne
  getByPersonne: async (personneId) => {
    try {
      const response = await api.get(`/temps-consommes/personne/${personneId}`);
      return response.data;
    } catch (error) {
      throw new Error(`Erreur lors de la récupération des temps consommés de la personne ${personneId}`);
    }
  },

  // Créer ou mettre à jour les temps consommés
  upsert: async (projetId, personneId, semaine, joursConsommes, facturable, commentaire, phase) => {
    try {
      const response = await api.post(`/temps-consommes`, {
        id_projet: projetId,
        id_personne: personneId,
        semaine,
        jours_consommes: joursConsommes,
        facturable,
        commentaire,
        phase,
      });
      return response.data.data; // Extraire les temps consommés du wrapper API
    } catch (error) {
      throw new Error('Erreur lors de la sauvegarde des temps consommés');
    }
  },

  // Supprimer les temps consommés
  delete: async (id) => {
    try {
      await api.delete(`/temps-consommes/${id}`);
    } catch (error) {
      throw new Error(`Erreur lors de la suppression des temps consommés ${id}`);
    }
  },
};

// Services pour l'authentification
export const authService = {
  // Inscription d'un nouvel utilisateur
  register: async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erreur lors de l\'inscription');
    }
  },

  // Connexion utilisateur
  login: async (credentials) => {
    try {
      console.log('🔍 Service API - Login avec credentials:', {
        email: credentials.email
      });
      
      const response = await api.post('/auth/login', credentials);
      const { token, utilisateur } = response.data.data;
      
      console.log('✅ Connexion réussie, token reçu:', !!token);
      
      // Stocker le token dans localStorage
      if (token) {
        localStorage.setItem('authToken', token);
        // Ajouter le token aux headers par défaut d'axios
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      
      return { token, user: utilisateur };
    } catch (error) {
      console.error('❌ Erreur de connexion API:', error.response?.data);
      throw new Error(error.response?.data?.message || 'Erreur lors de la connexion');
    }
  },

  // Déconnexion
  logout: () => {
    localStorage.removeItem('authToken');
    delete api.defaults.headers.common['Authorization'];
  },

  // Vérification du token
  verify: async () => {
    try {
      const response = await api.get('/auth/verify');
      // Le backend retourne { success: true, message: 'Token valide', data: user }
      return { user: response.data.data };
    } catch (error) {
      // Si le token n'est pas valide, on nettoie
      authService.logout();
      throw new Error('Token invalide');
    }
  },

  // Récupération du profil utilisateur
  getProfile: async () => {
    try {
      const response = await api.get('/auth/profile');
      return response.data.data || response.data;
    } catch (error) {
      throw new Error('Erreur lors de la récupération du profil');
    }
  },

  // Mise à jour du profil utilisateur
  updateProfile: async (profileData) => {
    try {
      const response = await api.put('/auth/profile', profileData);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erreur lors de la mise à jour du profil');
    }
  },

  // Modification du mot de passe
  changePassword: async (passwordData) => {
    try {
      const response = await api.put('/auth/change-password', passwordData);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erreur lors de la modification du mot de passe');
    }
  },

  // Initialiser le token depuis localStorage au démarrage
  initializeAuth: () => {
    const token = localStorage.getItem('authToken');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      return token;
    }
    return null;
  },
};

// Services pour Stripe
export const stripeService = {
  // Créer une session de checkout Stripe
  createCheckoutSession: async (lookupKey) => {
    try {
      const response = await api.post('/stripe/create-checkout-session', {
        lookup_key: lookupKey
      });
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erreur lors de la création de la session de paiement');
    }
  },

  // Créer une session de portail client
  createPortalSession: async () => {
    try {
      const response = await api.post('/stripe/create-portal-session');
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erreur lors de la création de la session de portail');
    }
  },

  // Récupérer les abonnements de l'utilisateur connecté
  getUserSubscriptions: async () => {
    try {
      const response = await api.get('/stripe/my-subscriptions');
      return response.data || response;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erreur lors de la récupération de vos abonnements');
    }
  },

  // Récupérer l'abonnement de l'utilisateur connecté
  getCurrentUserSubscription: async (stripeClientId) => {
    try {
      const response = await api.get(`/stripe/customer/${stripeClientId}/subscriptions`);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erreur lors de la récupération de votre abonnement');
    }
  },

  // Récupérer le statut d'abonnement de l'utilisateur connecté
  getSubscriptionStatus: async () => {
    try {
      const response = await api.get('/stripe/subscription-status');
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erreur lors de la récupération du statut d\'abonnement');
    }
  },

  // Récupérer les informations d'un abonnement
  getSubscription: async (subscriptionId) => {
    try {
      const response = await api.get(`/stripe/subscription/${subscriptionId}`);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(`Erreur lors de la récupération de l'abonnement ${subscriptionId}`);
    }
  },

  // Récupérer les abonnements d'un client
  getCustomerSubscriptions: async (customerId) => {
    try {
      const response = await api.get(`/stripe/customer/${customerId}/subscriptions`);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(`Erreur lors de la récupération des abonnements du client ${customerId}`);
    }
  },
};

// Services pour les équipes
export const equipesService = {
  // Récupérer mes équipes
  getMesEquipes: async () => {
    try {
      const response = await api.get('/equipes');
      return response.data;
    } catch (error) {
      throw new Error('Erreur lors de la récupération des équipes');
    }
  },

  // Récupérer une équipe par ID
  getById: async (id) => {
    try {
      const response = await api.get(`/equipes/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(`Erreur lors de la récupération de l'équipe ${id}`);
    }
  },

  // Créer une nouvelle équipe
  create: async (equipeData) => {
    try {
      const response = await api.post('/equipes', equipeData);
      return response.data.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erreur lors de la création de l\'équipe');
    }
  },

  // Modifier une équipe
  update: async (id, equipeData) => {
    try {
      const response = await api.put(`/equipes/${id}`, equipeData);
      return response.data.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erreur lors de la modification de l\'équipe');
    }
  },

  // Inviter un membre
  inviterMembre: async (equipeId, invitationData) => {
    try {
      const response = await api.post(`/equipes/${equipeId}/inviter`, invitationData);
      return response.data.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erreur lors de l\'invitation');
    }
  },

  // Récupérer les membres d'une équipe
  getMembres: async (equipeId) => {
    try {
      const response = await api.get(`/equipes/${equipeId}/membres`);
      return response.data;
    } catch (error) {
      throw new Error(`Erreur lors de la récupération des membres de l'équipe ${equipeId}`);
    }
  },

  // Modifier le rôle d'un membre
  modifierRoleMembre: async (equipeId, membreId, roleCode) => {
    try {
      const response = await api.put(`/equipes/${equipeId}/membres/${membreId}/role`, {
        role_code: roleCode
      });
      return response.data.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erreur lors de la modification du rôle');
    }
  },

  // Quitter une équipe
  quitter: async (equipeId) => {
    try {
      const response = await api.post(`/equipes/${equipeId}/quitter`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erreur lors de la sortie de l\'équipe');
    }
  },

  // Récupérer la liste des rôles disponibles
  getRoles: async () => {
    try {
      const response = await api.get('/equipes/roles');
      return response.data;
    } catch (error) {
      throw new Error('Erreur lors de la récupération des rôles');
    }
  }
};

// Services pour les invitations
export const invitationsService = {
  // Récupérer une invitation par token (public)
  getByToken: async (token) => {
    try {
      const response = await api.get(`/invitations/token/${token}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erreur lors de la récupération de l\'invitation');
    }
  },

  // Accepter une invitation (public)
  accepter: async (token, donneesUtilisateur = null) => {
    try {
      const response = await api.post(`/invitations/${token}/accepter`, donneesUtilisateur);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erreur lors de l\'acceptation de l\'invitation');
    }
  },

  // Refuser une invitation (public)
  refuser: async (token) => {
    try {
      const response = await api.post(`/invitations/${token}/refuser`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erreur lors du refus de l\'invitation');
    }
  },

  // Récupérer mes invitations en attente (authentifié)
  getMesInvitations: async () => {
    try {
      const response = await api.get('/invitations/mes-invitations');
      return response.data;
    } catch (error) {
      throw new Error('Erreur lors de la récupération de vos invitations');
    }
  },

  // Annuler une invitation (authentifié)
  annuler: async (invitationId) => {
    try {
      const response = await api.delete(`/invitations/${invitationId}/annuler`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erreur lors de l\'annulation de l\'invitation');
    }
  },

  // Renouveler une invitation (authentifié)
  renouveler: async (invitationId) => {
    try {
      const response = await api.post(`/invitations/${invitationId}/renouveler`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Erreur lors du renouvellement de l\'invitation');
    }
  }
};

// Services pour les vacances
export const vacancesService = {
  // Récupérer toutes les vacances
  getAll: async (params = {}) => {
    try {
      const response = await api.get('/vacances', { params });
      return response.data;
    } catch (error) {
      throw new Error('Erreur lors de la récupération des vacances');
    }
  },

  // Récupérer les vacances d'une personne
  getByPersonne: async (personneId, params = {}) => {
    try {
      const response = await api.get(`/vacances/personne/${personneId}`, { params });
      return response.data;
    } catch (error) {
      throw new Error(`Erreur lors de la récupération des vacances de la personne ${personneId}`);
    }
  },

  // Créer ou mettre à jour des vacances
  createOrUpdate: async (vacancesData) => {
    try {
      const response = await api.post('/vacances', vacancesData);
      return response.data;
    } catch (error) {
      throw new Error('Erreur lors de la sauvegarde des vacances');
    }
  },

  // Supprimer des vacances par ID
  delete: async (id) => {
    try {
      await api.delete(`/vacances/${id}`);
    } catch (error) {
      throw new Error(`Erreur lors de la suppression des vacances ${id}`);
    }
  },

  // Supprimer les vacances d'une semaine pour une personne
  deleteBySemaine: async (semaine, personneId) => {
    try {
      await api.delete(`/vacances/semaine/${semaine}`, { 
        params: { id_personne: personneId } 
      });
    } catch (error) {
      throw new Error(`Erreur lors de la suppression des vacances de la semaine ${semaine}`);
    }
  },
};

export default api; 